import axios from 'axios';
import Cookies from 'js-cookie';

import backEnd from '../constants/backEnd';
import endpoints from '../constants/endpoints';

const GENERATION_ENDPOINTS = [
  '/api/openai/chatv2/',
  '/api/openai/dalle-v2/',
  '/api/openai/image-agent/',
  '/api/openai/calendar/generate/',
];

const api = axios.create({
  baseURL: '',
});

const getStoredSession = () => {
  try {
    const raw = Cookies.get('airton@data');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveStoredSession = (session) => {
  Cookies.set('airton@data', JSON.stringify(session), { expires: 90 });
};

const clearStoredSession = () => {
  Cookies.remove('airton@data');
};

const applyAccessToken = (token) => {
  api.defaults.headers.common.Authorization = token ? `Bearer ${token}` : 'Bearer ';
};

const initialSession = getStoredSession();
if (initialSession?.token) {
  applyAccessToken(initialSession.token);
}

let refreshPromise = null;

const refreshAccessToken = async () => {
  const session = getStoredSession();
  const refresh = session?.refresh;

  if (!refresh) {
    throw new Error('Missing refresh token');
  }

  const response = await axios.post(
    `${backEnd.url}${endpoints.access.tokenRefresh}`,
    { refresh },
    { headers: { 'Content-Type': 'application/json' } }
  );

  const nextAccess = response.data?.access;
  const nextRefresh = response.data?.refresh || refresh;

  if (!nextAccess) {
    throw new Error('Missing access token in refresh response');
  }

  const updatedSession = {
    ...session,
    token: nextAccess,
    refresh: nextRefresh,
  };

  saveStoredSession(updatedSession);
  applyAccessToken(nextAccess);
  return nextAccess;
};

api.interceptors.response.use(
  (response) => {
    const url = response.config?.url || '';
    const isGeneration = GENERATION_ENDPOINTS.some(ep => url.includes(ep));
    if (isGeneration && response.status === 200) {
      window.dispatchEvent(new CustomEvent('admind_generation_complete'));
    }
    return response;
  },
  async (error) => {
    if (error.response && error.response.status === 402) {
      const data = error.response.data || {};
      window.dispatchEvent(new CustomEvent('admind_limit_exceeded', {
        detail: {
          code: data.code || 'free_limit_exceeded',
          limit_type: data.limit_type || 'tokens',
          has_stripe_customer: data.has_stripe_customer === true,
          current_plan: data.current_plan || null,
        }
      }));
    }

    const originalRequest = error.config || {};
    const errorCode = error.response?.data?.code;
    const shouldTryRefresh =
      !originalRequest._retry &&
      !String(originalRequest.url || '').includes(endpoints.access.tokenRefresh) &&
      !String(originalRequest.url || '').includes(endpoints.access.login) &&
      !String(originalRequest.url || '').includes(endpoints.access.loginGoogle) &&
      !String(originalRequest.url || '').includes(endpoints.access.loginFacebook) &&
      (error.response?.status === 401 || errorCode === 'token_not_valid');

    if (shouldTryRefresh) {
      originalRequest._retry = true;

      try {
        refreshPromise = refreshPromise || refreshAccessToken();
        const nextAccess = await refreshPromise;
        refreshPromise = null;

        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${nextAccess}`;
        return api(originalRequest);
      } catch (refreshError) {
        refreshPromise = null;
        clearStoredSession();
        applyAccessToken('');
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
